import { useMemo } from 'react';
import { QueryHookOptions } from '@apollo/client';
import { Paths } from 'type-fest';

import { usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import {
  ExternalLoyaltyManualOfferFilter,
  External_Loyalty_Manual_Offer_Period,
  GetExternalLoyaltyManualOffersQuery,
  useGetExternalLoyaltyManualOffersQuery,
} from '@occc/graphql';

import { ExternalLoyaltyManualOffer, OfferTableType } from '../tobacco-api-offers.types';
import { ROWS_PER_PAGE, TOBACCO_OFFERS_LIST_POLL_INTERVAL_MS } from '../tobacco-api-offers.constants';

import { usePollingControl } from './use-polling-control.hook';

const PAGE_INFO_PATH =
  'externalLoyaltyManualOffers.pageInfo' satisfies Paths<GetExternalLoyaltyManualOffersQuery>;

const BASE_FILTERS: Record<OfferTableType, ExternalLoyaltyManualOfferFilter> = {
  active: {
    isInActivate: true,
    periodEquals: External_Loyalty_Manual_Offer_Period.Current,
  },
  inactive: {
    isInActivate: false,
    periodEquals: External_Loyalty_Manual_Offer_Period.Current,
  },
  upcoming: {
    periodEquals: External_Loyalty_Manual_Offer_Period.Next,
  },
};

const mapEdgesToOffers = (
  data: GetExternalLoyaltyManualOffersQuery | undefined,
): ExternalLoyaltyManualOffer[] | undefined => {
  if (!data) return undefined;

  return data.externalLoyaltyManualOffers.edges.map(({ node }) => node);
};

type UseTobaccoApiOffersPaginationParams = {
  tableType: OfferTableType;
  userFilter?: ExternalLoyaltyManualOfferFilter;
  isPollingEnabled?: boolean;
  pollInterval?: number;
};

export const useTobaccoApiOffersPagination = ({
  tableType,
  userFilter,
  isPollingEnabled = false,
  pollInterval = TOBACCO_OFFERS_LIST_POLL_INTERVAL_MS,
}: UseTobaccoApiOffersPaginationParams) => {
  const [paginationInfo, onPageChange] = usePaginationParams({
    pageSize: ROWS_PER_PAGE,
    afterParamName: `${tableType}After`,
    beforeParamName: `${tableType}Before`,
  });

  const filter: ExternalLoyaltyManualOfferFilter = useMemo(
    () => ({
      ...BASE_FILTERS[tableType],
      ...userFilter,
    }),
    [tableType, userFilter],
  );

  const response = usePaginatedQuery(
    useGetExternalLoyaltyManualOffersQuery,
    PAGE_INFO_PATH,
    ROWS_PER_PAGE,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: { filter },
      ...(isPollingEnabled ? { pollInterval } : {}),
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  usePollingControl(isPollingEnabled, response.stopPolling);

  const offers = useMemo(() => mapEdgesToOffers(response.data), [response.data]);

  return {
    data: offers,
    loading: response.loading,
    error: response.error,
    paginationProps: response.paginationProps,
  };
};
